import { Menu, Tooltip } from '@chakra-ui/react';
import { TypeSquare } from '@kula-ai/icons-core';
import type { Editor } from '@tiptap/react';
import { colors } from '@/theme/foundations/colors';
import {
  ToolbarSelectMenuItem,
  ToolbarSelectMenuList,
  ToolbarSelectTrigger,
} from './EditorToolbarSelect';

// Web-safe fonts only: each maps to a full fallback stack so the styling
// renders consistently across Gmail, Outlook (Word engine) and Apple Mail.
// Custom/Google fonts are intentionally excluded — email clients would just
// fall back, defeating the purpose.
export const FONT_FAMILY_PRESETS = [
  { label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
  { label: 'Trebuchet MS', stack: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: 'Georgia', stack: "Georgia, 'Times New Roman', serif" },
  { label: 'Times New Roman', stack: "'Times New Roman', Times, serif" },
  { label: 'Courier New', stack: "'Courier New', Courier, monospace" },
] as const;

// Returns the matched preset label for the current selection, or null when no
// font family is applied (the document-default state). Kept distinct from the
// trigger's fallback label so the "Default" item only highlights when the
// selection is genuinely unset — not when the default font (Arial) is applied.
const getActiveLabel = (editor?: Editor) => {
  const raw = editor?.getAttributes('textStyle')?.fontFamily as
    | string
    | undefined;
  if (!raw) {
    return null;
  }
  // Match on the leading family name so stored stacks resolve back to a label
  // regardless of quote style.
  const normalized = raw.replace(/['"]/g, '').toLowerCase();
  const match = FONT_FAMILY_PRESETS.find((p) =>
    normalized.startsWith(p.label.toLowerCase())
  );
  return match?.label ?? null;
};

type EditorFontFamilyProps = {
  editor?: Editor;
  isTextSelected?: boolean;
};

export const EditorFontFamily = ({ editor }: EditorFontFamilyProps) => {
  if (!editor) {
    return null;
  }

  const activeLabel = getActiveLabel(editor);
  // No font applied → genuine unset state (the "Default" menu item).
  const isDefault = activeLabel === null;
  // Icon-only trigger (mirrors the heading buttons) keeps the toolbar compact;
  // the active font name surfaces in the tooltip on hover since there's no
  // visible label.
  const tooltipLabel = isDefault
    ? 'Font family'
    : `Font family: ${activeLabel}`;

  const applyFont = (stack: string) => {
    editor.chain().focus().setFontFamily(stack).run();
  };

  const clearFont = () => {
    editor.chain().focus().unsetFontFamily().run();
  };

  return (
    <Menu gutter={2} placement="bottom-start">
      <Tooltip
        label={tooltipLabel}
        arrowSize={8}
        hasArrow
        placement="top"
        variant="ats-primary"
        borderRadius="4px"
      >
        <ToolbarSelectTrigger
          px="4px"
          minW="auto"
          data-test-id="editor-toolbar-fontFamily-button"
        >
          {/* Active selection darkens the icon (secondary-900) instead of a
              grey background block, mirroring the kula Email editor control. */}
          <TypeSquare
            size={16}
            color={
              isDefault ? colors['secondary-700'] : colors['secondary-900']
            }
          />
        </ToolbarSelectTrigger>
      </Tooltip>
      <ToolbarSelectMenuList minW="168px">
        <ToolbarSelectMenuItem
          isSelected={isDefault}
          onClick={clearFont}
          data-test-id="editor-toolbar-fontFamily-default"
        >
          Default
        </ToolbarSelectMenuItem>
        {FONT_FAMILY_PRESETS.map(({ label, stack }) => (
          <ToolbarSelectMenuItem
            key={label}
            isSelected={label === activeLabel}
            onClick={() => applyFont(stack)}
            fontFamily={stack}
            data-test-id={`editor-toolbar-fontFamily-${label}`}
          >
            {label}
          </ToolbarSelectMenuItem>
        ))}
      </ToolbarSelectMenuList>
    </Menu>
  );
};
